import { useState, useEffect } from 'react';
import api from '../lib/api';

const COL_WIDTHS = {
  Skit:          ['Character', 'Line', 'Tone', 'Props', 'Scene Description'],
  TH:            ['Section', 'Hook/Title', 'Script', 'Notes/VFX', 'TH'],
  AI:            ['Section', 'Hook/Title', 'Script', 'Notes/VFX'],
  Mockumentary:  ['Character/Speaker', 'Line/Narration', 'Directional Notes', 'Scene Description', 'VFX', 'Props'],
  Longform:      ['Scene #', 'Character', 'Line', 'Scene Description'],
};

function ScriptTable({ script }) {
  const body = script.scriptBody;
  if (!body || !body.rows) {
    return <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '20px 0' }}>Script body not available.</div>;
  }

  const headers = body.headers || COL_WIDTHS[script.scriptFormat] || [];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)', marginTop: 12 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid var(--border)', color: 'var(--tx-muted)', fontWeight: 600, whiteSpace: 'nowrap', background: 'var(--bg-card)' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid var(--border)' }}>
              {(Array.isArray(row) ? row : Object.values(row)).map((cell, ci) => (
                <td key={ci} style={{ padding: '8px 12px', color: 'var(--tx-primary)', verticalAlign: 'top', lineHeight: 1.5 }}>
                  {typeof cell === 'string' && cell.startsWith('http') ? (
                    <a href={cell} target="_blank" rel="noreferrer" style={{ color: 'var(--violet)', textDecoration: 'none' }}>
                      ↗ Open link
                    </a>
                  ) : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TalentScriptView() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(null);

  useEffect(() => {
    api.get('/api/talent-portal/scripts')
      .then(r => setScripts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handlePrint(script) {
    const win = window.open('', '_blank');
    const tableHtml = script.scriptBody ? buildTableHTML(script.scriptBody) : '<p>No script body.</p>';
    win.document.write(
      '<!DOCTYPE html><html><head><title>' + script.scriptName + '</title>' +
      '<style>body{font-family:"Courier New",monospace;font-size:12px;margin:40px}' +
      'h1{font-size:18px;margin-bottom:4px}.sub{color:#666;font-size:12px;margin-bottom:24px}' +
      'table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px 10px;text-align:left;vertical-align:top}' +
      'th{background:#f5f5f5;font-weight:bold}@media print{body{margin:20px}}</style>' +
      '</head><body>' +
      '<h1>' + script.scriptName + '</h1>' +
      '<div class="sub">' + (script.clientName || '') + '</div>' +
      tableHtml +
      '</body></html>'
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  }

  function buildTableHTML(body) {
    if (!body || !body.rows) return '';
    const headers = body.headers || [];
    const ths = headers.map(h => `<th>${h}</th>`).join('');
    const trs = body.rows.map(row => {
      const cells = (Array.isArray(row) ? row : Object.values(row))
        .map(c => `<td>${c || ''}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
  }

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">My Scripts</div>
          <div className="page-sub">Approved scripts assigned to your upcoming shoots</div>
        </div>
      </div>

      <div className="body">
        {loading ? (
          <div style={{ color: 'var(--tx-muted)', fontSize: 13 }}>Loading…</div>
        ) : scripts.length === 0 ? (
          <div className="card" style={{ padding: '32px 24px', textAlign: 'center', maxWidth: 480 }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
            <div style={{ fontSize: 14, color: 'var(--tx-secondary)', fontWeight: 600 }}>No scripts available yet</div>
            <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 6, lineHeight: 1.6 }}>
              Scripts will appear here once they're approved and assigned to your upcoming shoots.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 900 }}>
            {scripts.map(s => (
              <div key={s.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  style={{ padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}
                  onClick={() => setOpen(open === s.id ? null : s.id)}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--tx-primary)' }}>{s.scriptName}</div>
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 3 }}>{s.clientName}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <span className="pill pill-approved" style={{ fontSize: 10 }}>Approved</span>
                    {s.sourcePdfName && (
                      <button
                        className="btn-ghost"
                        data-testid="talent-series-pdf"
                        style={{ fontSize: 11, padding: '4px 10px', color: 'var(--violet)' }}
                        onClick={async e => { e.stopPropagation(); try { const r = await api.get(`/api/talent-portal/scripts/${s.id}/pdf`); window.open(r.data.url, '_blank', 'noopener'); } catch { alert('Could not open the screenplay PDF.'); } }}
                        title={`Download the series screenplay — ${s.sourcePdfName}`}
                      >
                        ↓ Screenplay PDF
                      </button>
                    )}
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: '4px 10px' }}
                      onClick={e => { e.stopPropagation(); handlePrint(s); }}
                      title="Download / Print PDF"
                    >
                      ↓ PDF
                    </button>
                    <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>{open === s.id ? '▲' : '▼'}</span>
                  </div>
                </div>
                {open === s.id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '0 22px 22px' }}>
                    <ScriptTable script={s} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
